//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer do Portal LiveModa para a plataforma SmartFlow.
// Gravação REAL do Parceiro de Negócio no SAP Business One (Service Layer), sendo o SAP o mandante
// e o MongoDB (SmartFlowParceiroStaging, via smartflow-backend) o espelho.
//
// Diferenças em relação ao portal (services/smartflow/smartflowSapWrite.js):
//  - A conexão SAP é a do PROJETO (SmartFlowProject.serviceLayerUrl/User/Password/DataBase). O
//    login (/Login) é feito pela tela do Importer antes de chamar estas funções (mesmo padrão do
//    ProjectConsole) — o cookie B1SESSION fica ativo. Toda função aqui recebe `serviceLayerUrl`
//    porque o apiSap do SmartFlow não tem baseURL (ver services/sapApi.js) — as URLs são absolutas.
//  - O staging (doc do Mongo) vem em camelCase (cardCode, federalTaxId, enderecoStreet, ...),
//    não snake_case.
//  - A resolução de município (OCNT) e a marcação "gravado_sap" + log passam pelo backend
//    (services/smartflow/importerApi.js), não pelo Supabase.
//
// O mapeamento de campos do SAP (TaxId0=CNPJ, TaxId1=IE, TaxId4=CPF, TaxId5=ID estrangeiro;
// BPAddresses Principal/Entrega; Estado "EX" por país via POST /States; município "Exterior" por
// país no mapa fixo) foi confirmado ao vivo contra o tenant SBODEMOBRMODA no portal — revalidar
// ao rodar contra um tenant SAP diferente.
//======
import apiSap from "services/sapApi"
import { resolverMunicipiosSap, marcarParceiroGravadoNoSap } from "services/smartflow/importerApi"

// apiSap do SmartFlow não tem baseURL — monta a URL absoluta da Service Layer do projeto.
function sapUrl(serviceLayerUrl, path) {
  return String(serviceLayerUrl || "").replace(/\/$/, "") + path
}

//====== WR data 03/09/2026 (porte)
// Lista real de Grupos de Parceiro de Negócio do tenant do projeto (GET /BusinessPartnerGroups),
// já filtrada por papel (bbpgt_VendorGroup / bbpgt_CustomerGroup) — usada nos combobox de Grupo
// do modal "Alterando Projeto".
// @param {"fornecedor"|"cliente"} [tipo]
// @param {string} serviceLayerUrl
// @return {Promise<Array<{code:number, name:string, type:string}>>}
//======
export async function buscarGruposParceiroSap(tipo, serviceLayerUrl) {
  const { data } = await apiSap.get(
    sapUrl(serviceLayerUrl, "/BusinessPartnerGroups?$select=Code,Name,Type&$orderby=Name")
  )
  const grupos = (data?.value || []).map((grupo) => ({
    code: grupo.Code,
    name: grupo.Name,
    type: grupo.Type,
  }))

  if (tipo === "cliente") return grupos.filter((g) => g.type === "bbpgt_CustomerGroup")
  if (tipo === "fornecedor") return grupos.filter((g) => g.type === "bbpgt_VendorGroup")
  return grupos
}

// Cruza o código IBGE do parceiro contra a OCNT (via backend) pra achar o AbsId que
// BPAddresses.County do SAP espera. Sem código / sem match -> null (Município fica vazio).
async function buscarSapAbsIdMunicipio(codigoMunicipioIbge) {
  if (!codigoMunicipioIbge) return null
  try {
    const mapa = await resolverMunicipiosSap([codigoMunicipioIbge])
    return mapa?.[codigoMunicipioIbge]?.absId || null
  } catch (error) {
    console.error("[SmartFlow] Falha ao resolver município (OCNT):", error)
    return null
  }
}

// Documento nacional: CPF (11 díg) -> TaxId4; CNPJ (14 díg) ou outro -> TaxId0. Nunca os dois.
function montarCamposDocumentoFiscal(documento) {
  const digits = String(documento || "").replace(/\D/g, "")
  if (!digits) return {}
  return digits.length === 11 ? { TaxId4: documento } : { TaxId0: documento }
}

function montarCampoIdEstrangeiro(idEstrangeiro) {
  const valor = String(idEstrangeiro || "").trim()
  return valor ? { TaxId5: valor } : {}
}

// "EX" é uma sigla de Estado vinculada (Code+Country) — não existe pra "BR". A Service Layer
// aceita POST /States: verifica se a combinação já existe e cria sob demanda.
async function garantirEstadoExVinculado(serviceLayerUrl, countryCode) {
  if (!countryCode) return false

  try {
    await apiSap.get(sapUrl(serviceLayerUrl, `/States(Code='EX',Country='${countryCode}')`))
    return true
  } catch (error) {
    if (!error?.warning) {
      console.error(
        `[SmartFlow] Falha ao consultar Estado "EX" pro país ${countryCode} — endereço sem Estado/Município:`,
        error
      )
      return false
    }
  }

  try {
    await apiSap.post(sapUrl(serviceLayerUrl, "/States"), {
      Code: "EX",
      Country: countryCode,
      Name: "EX",
    })
    return true
  } catch (error) {
    console.error(
      `[SmartFlow] Falha ao criar Estado "EX" pro país ${countryCode} — endereço sem Estado/Município:`,
      error
    )
    return false
  }
}

//====== WR data 03/09/2026 (porte)
// Cada país tem seu PRÓPRIO município "Exterior" na OCNT, com AbsId distinto — e a Service Layer
// não expõe entidade de Município (não dá pra criar sob demanda como o Estado). País novo precisa
// do cadastro manual no SAP desktop, e o AbsId resultante adicionado aqui. País fora do mapa:
// Município fica de fora (só Estado "EX").
//======
const MUNICIPIOS_EXTERIOR_POR_PAIS = {
  CN: "5576", // China
  US: "5578", // Estados Unidos
}

async function montarPayloadBusinessPartner(serviceLayerUrl, parceiro) {
  const temEndereco = !!(
    parceiro.enderecoStreet ||
    parceiro.enderecoCity ||
    parceiro.enderecoZipCode
  )

  const payload = {
    CardCode: parceiro.cardCode,
    CardType: parceiro.cardType || "cSupplier",
    CardName: parceiro.cardName || undefined,
    CardForeignName: parceiro.cardForeignName || undefined,
    FederalTaxID: parceiro.federalTaxId || undefined,
    Valid: parceiro.valid || "tYES",
    EmailAddress: parceiro.email || undefined,
    Phone1: parceiro.telefone || undefined,
    GroupCode: parceiro.groupCode || undefined,
  }

  if (temEndereco) {
    const sapAbsIdMunicipio = await buscarSapAbsIdMunicipio(parceiro.codigoMunicipioIbge)

    // "BR" é o fallback de país não identificado — nunca criar Estado "EX" pra "BR".
    const estadoExValido =
      parceiro.enderecoCounty === "EX" &&
      parceiro.enderecoCountry &&
      parceiro.enderecoCountry !== "BR" &&
      (await garantirEstadoExVinculado(serviceLayerUrl, parceiro.enderecoCountry))

    const camposEndereco = {
      Street: parceiro.enderecoStreet || undefined,
      StreetNo: parceiro.enderecoStreetNo || undefined,
      TypeOfAddress: parceiro.tipoLogradouro || undefined,
      BuildingFloorRoom: parceiro.enderecoComplemento || undefined,
      Block: parceiro.enderecoBlock || undefined,
      City: parceiro.enderecoCity || undefined,
      State: estadoExValido
        ? "EX"
        : parceiro.enderecoCounty && parceiro.enderecoCounty !== "EX"
        ? parceiro.enderecoCounty
        : undefined,
      County: estadoExValido
        ? MUNICIPIOS_EXTERIOR_POR_PAIS[parceiro.enderecoCountry]
        : sapAbsIdMunicipio
        ? String(sapAbsIdMunicipio)
        : undefined,
      ZipCode: parceiro.enderecoZipCode || undefined,
      Country: parceiro.enderecoCountry || "BR",
    }

    payload.BPAddresses = [
      { AddressName: "Principal", AddressType: "bo_BillTo", ...camposEndereco },
      { AddressName: "Entrega", AddressType: "bo_ShipTo", ...camposEndereco },
    ]
  }

  if (parceiro.federalTaxId || parceiro.taxIdEstrangeiro) {
    const camposFiscais = {
      ...montarCamposDocumentoFiscal(parceiro.federalTaxId),
      ...montarCampoIdEstrangeiro(parceiro.taxIdEstrangeiro),
      TaxId1: parceiro.inscricaoEstadual || undefined,
    }

    payload.BPFiscalTaxIDCollection = [
      { Address: "Principal", AddrType: "bo_BillTo", ...camposFiscais },
      { Address: "Entrega", AddrType: "bo_ShipTo", ...camposFiscais },
    ]
  }

  return payload
}

//====== WR data 03/09/2026 (porte)
// Exclui o Parceiro de Negócio de verdade no SAP — o tenant valida CNPJ único entre Parceiros, um
// registro "preso" impede reimportar o mesmo CNPJ. Idempotente (404 não é erro). Exportada
// separada de excluirParceiroStaging (backend) — a tela chama as duas.
// @param {string} cardCode
// @param {string} serviceLayerUrl
//======
export async function excluirParceiroDoSap(cardCode, serviceLayerUrl) {
  try {
    await apiSap.delete(sapUrl(serviceLayerUrl, `/BusinessPartners('${cardCode}')`))
  } catch (error) {
    // o interceptor do apiSap já transforma o -2028/404 em { warning: true } antes de chegar aqui
    if (error?.warning) return
    throw new Error(extrairMensagemErroSap(error))
  }
}

function extrairMensagemErroSap(error) {
  return (
    error?.response?.data?.error?.message?.value ||
    error?.response?.data?.error?.message ||
    error?.message ||
    "Falha ao gravar no SAP."
  )
}

// GET por CardCode: só um "não encontrado" de verdade (error.warning, sinal que o sapSLErrorFactory
// usa pro -2028) cai pro POST; qualquer outro erro é repropagado (não adivinhar POST vs PATCH).
async function parceiroJaExisteNoSap(serviceLayerUrl, cardCode) {
  try {
    await apiSap.get(sapUrl(serviceLayerUrl, `/BusinessPartners('${cardCode}')`))
    return true
  } catch (error) {
    if (error?.warning) return false
    throw error
  }
}

//====== WR data 03/09/2026
// Motivo da alteração: bug reportado pelo usuário (com print) — "Erro interno This CNPJ already
// exists , 'F00266'": o CNPJ já existe no SAP sob OUTRO CardCode (gravado direto no SAP, ou por
// uma tentativa anterior do SmartFlow que não marcou a staging). Procura o Parceiro pelo CNPJ
// (FederalTaxID) e devolve o CardCode real, pra a gravação virar uma ATUALIZAÇÃO desse CardCode
// em vez de tentar criar um novo. Sem match / erro -> null.
// @param {string} cnpjDigits só dígitos
// @return {Promise<string|null>}
//======
export async function buscarCardCodePorCnpj(serviceLayerUrl, cnpjDigits) {
  const digits = String(cnpjDigits || "").replace(/\D/g, "")
  if (!digits) return null
  try {
    const { data } = await apiSap.get(
      sapUrl(
        serviceLayerUrl,
        `/BusinessPartners?$select=CardCode,FederalTaxID&$filter=${encodeURIComponent(
          `FederalTaxID eq '${digits}'`
        )}&$top=1`
      )
    )
    return data?.value?.[0]?.CardCode || null
  } catch (error) {
    console.error("[SmartFlow] Falha ao procurar Parceiro por CNPJ no SAP:", error)
    return null
  }
}

//====== WR data 09/09/2026
// Motivo da alteração: pedido do usuário — "ele deveria identificar estas divergências ANTES de
// tentar gravar". Consulta em LOTE o SAP por CNPJ/CPF (FederalTaxID), pra "Preparar dados" já
// marcar as linhas que vão virar ATUALIZAÇÃO (com o CardCode real) em vez de o usuário só
// descobrir no erro do "Gravar SAP". O SAP valida CNPJ/CPF único entre Parceiros — se o documento
// já existe sob outro CardCode, a criação falha (-2035/-5002), então é o mesmo caso do
// buscarCardCodePorCnpj, só que resolvido antecipadamente e pra vários de uma vez.
// @param {Array<string>} listaDigits CNPJ/CPF só dígitos
// @return {Promise<Object>} { [digits]: cardCode } só dos que já existem no SAP
//======
export async function buscarCardCodesPorDocumentos(serviceLayerUrl, listaDigits) {
  const unicos = [
    ...new Set((listaDigits || []).map((d) => String(d || "").replace(/\D/g, "")).filter(Boolean)),
  ]
  if (!unicos.length) return {}

  const mapa = {}
  // Quebra em blocos pra não estourar o tamanho da URL do $filter.
  const BLOCO = 15
  for (let i = 0; i < unicos.length; i += BLOCO) {
    const parte = unicos.slice(i, i + BLOCO)
    const filtro = parte.map((d) => `FederalTaxID eq '${d}'`).join(" or ")
    try {
      const { data } = await apiSap.get(
        sapUrl(
          serviceLayerUrl,
          `/BusinessPartners?$select=CardCode,FederalTaxID&$filter=${encodeURIComponent(filtro)}`
        )
      )
      ;(data?.value || []).forEach((bp) => {
        const d = String(bp.FederalTaxID || "").replace(/\D/g, "")
        if (d && !mapa[d]) mapa[d] = bp.CardCode
      })
    } catch (error) {
      console.error("[SmartFlow] Falha na consulta em lote por CNPJ/CPF no SAP:", error)
    }
  }
  return mapa
}

// Popula a tela "Identif.fiscais" da aba GERAL (linha de BPFiscalTaxIDCollection com Address="")
// — objeto diferente da aba Endereços, precisa de um PATCH separado depois da criação/atualização.
async function atualizarFiscalPadraoDoParceiro(
  serviceLayerUrl,
  cardCode,
  { federalTaxId, inscricaoEstadual, taxIdEstrangeiro }
) {
  try {
    const { data } = await apiSap.get(
      sapUrl(serviceLayerUrl, `/BusinessPartners('${cardCode}')?$select=BPFiscalTaxIDCollection`)
    )
    const linhaAtual = (data?.BPFiscalTaxIDCollection || []).find((item) => item.Address === "")

    const { TaxId0 = null, TaxId4 = null } = montarCamposDocumentoFiscal(federalTaxId)
    const TaxId5 = String(taxIdEstrangeiro || "").trim() || null
    const linhaNova = linhaAtual
      ? { ...linhaAtual, TaxId0, TaxId4, TaxId5, TaxId1: inscricaoEstadual || null }
      : {
          Address: "",
          AddrType: "bo_ShipTo",
          TaxId0,
          TaxId4,
          TaxId5,
          TaxId1: inscricaoEstadual || null,
        }

    await apiSap.patch(sapUrl(serviceLayerUrl, `/BusinessPartners('${cardCode}')`), {
      BPFiscalTaxIDCollection: [linhaNova],
    })
  } catch (error) {
    console.error(
      "[SmartFlow] Falha ao atualizar Identif.fiscais (aba Geral) — CNPJ/IE por endereço já gravados normalmente:",
      error
    )
  }
}

//====== WR data 03/09/2026 (porte)
// Grava de verdade um Parceiro de Negócio no SAP — POST (criar) ou PATCH (atualizar), conforme o
// CardCode já existir. Depois, marca o doc de staging como 'gravado_sap' (via backend) e registra
// o log.
// @param {Object} parceiro doc completo do SmartFlowParceiroStaging (camelCase, com _id)
// @param {string|null} gravadoPor e-mail do usuário logado
// @param {string} serviceLayerUrl Service Layer do projeto (login já feito pela tela)
// @return {Promise<Object>} doc atualizado + acaoNoSap ("criado"|"atualizado")
//======
export async function gravarParceiroNoSap(parceiro, gravadoPor, serviceLayerUrl) {
  const payload = await montarPayloadBusinessPartner(serviceLayerUrl, parceiro)

  // PATCH nunca deve tentar trocar a chave — envia o CardCode do alvo (o real).
  const patchAlvo = (cc) =>
    apiSap.patch(sapUrl(serviceLayerUrl, `/BusinessPartners('${cc}')`), { ...payload, CardCode: cc })

  //====== WR data 03/09/2026, revisado 09/09/2026
  // Motivo da alteração: bugs reportados pelo usuário (com prints):
  //  - CardCode gerado já existe no SAP -> vira PATCH nele.
  //  - CNPJ/CPF já existe no SAP sob OUTRO CardCode -> procura o CardCode real pelo documento e
  //    faz o PATCH nele.
  // O SAP devolve mensagens ambíguas: "-2035 Esse código já foi cadastrado" pode ser TANTO
  // colisão de CardCode QUANTO de CNPJ/CPF (a localização BR valida documento único e às vezes
  // reporta como -2035 genérico, não -5002). Por isso o tratamento é o mesmo pros dois: em
  // qualquer erro de "já existe", (1) se o nosso CardCode existe de verdade -> PATCH nele;
  // (2) senão, procura pelo documento -> PATCH no CardCode real; (3) senão, erro real.
  //======
  const jaExisteNoInicio = await parceiroJaExisteNoSap(serviceLayerUrl, parceiro.cardCode)
  let acao = jaExisteNoInicio ? "atualizado" : "criado"
  let cardCodeFinal = parceiro.cardCode

  const resolverJaExiste = async (erroOriginal) => {
    // (1) o nosso CardCode existe? (a checagem inicial pode ter falhado por sessão/formato)
    if (await parceiroJaExisteNoSap(serviceLayerUrl, parceiro.cardCode)) {
      await patchAlvo(parceiro.cardCode)
      cardCodeFinal = parceiro.cardCode
      acao = "atualizado"
      return
    }
    // (2) o documento (CNPJ/CPF) já existe sob outro CardCode?
    const real = await buscarCardCodePorCnpj(serviceLayerUrl, parceiro.federalTaxId)
    if (real) {
      await patchAlvo(real)
      cardCodeFinal = real
      acao = "atualizado"
      return
    }
    // (3) não deu pra resolver — repropaga o erro original.
    throw erroOriginal
  }

  try {
    if (acao === "atualizado") {
      await patchAlvo(cardCodeFinal)
    } else {
      try {
        await apiSap.post(sapUrl(serviceLayerUrl, "/BusinessPartners"), payload)
      } catch (postError) {
        const msg = extrairMensagemErroSap(postError)
        if (/j[áa]\s+foi\s+cadastrado|-?2035|already exists|-?5002/i.test(msg)) {
          await resolverJaExiste(postError)
        } else {
          throw postError
        }
      }
    }
  } catch (error) {
    throw new Error(extrairMensagemErroSap(error))
  }

  if (parceiro.federalTaxId || parceiro.taxIdEstrangeiro) {
    await atualizarFiscalPadraoDoParceiro(serviceLayerUrl, cardCodeFinal, {
      federalTaxId: parceiro.federalTaxId,
      inscricaoEstadual: parceiro.inscricaoEstadual,
      taxIdEstrangeiro: parceiro.taxIdEstrangeiro,
    })
  }

  const atualizado = await marcarParceiroGravadoNoSap(parceiro._id, {
    gravadoPor: gravadoPor || null,
    cardCode: cardCodeFinal,
  })

  return { ...(atualizado || parceiro), cardCode: cardCodeFinal, acaoNoSap: acao }
}
